import * as React from "react";
import { Box, Chip, Menu, MenuItem, Avatar, AvatarProps } from "@mui/material";

import { useState } from "react";
import { useAccount } from "../../Account";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { UserView } from "../../AccountMenu";
import { BorrowerChild } from "@tlc/ls2pac/dto/BorrowerChild";

interface IAccountGuardianSelectorProps {
  //pass ID to update the selected user in the chip
  selectedId?: string | null;
  onSelect: (id: string | null) => void;
}

const getInitials = (user: UserView | BorrowerChild) =>
  `${(user?.firstName || "").charAt(0)}${(user?.lastName || "").charAt(0)}`;

interface GuardianAvatarProps extends AvatarProps {
  user: UserView | BorrowerChild;
}
const GuardianAvatar = ({ user, ...props }: GuardianAvatarProps) => {
  return <Avatar {...props}>{getInitials(user)}</Avatar>;
};

const AccountGuardianSelector: React.FC<IAccountGuardianSelectorProps> = ({
  selectedId,
  onSelect = () => {},
}) => {
  const { user, patron } = useAccount();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const accountsList: (BorrowerChild | UserView)[] = React.useMemo(() => {
    const children = patron?.children || [];
    return [user, ...children];
  }, [user, patron]);

  const selected = React.useMemo(() => {
    if (!selectedId) {
      // id param is null for the main account
      return user;
    }
    return (
      (patron?.children || []).find(
        (account) => account.id.toString() === selectedId
      ) || user
    );
  }, [selectedId, user, patron]);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleSelect = (id: UserView["id"] | BorrowerChild["id"]) => {
    onSelect(id === user.id ? null : id.toString());
    setAnchorEl(null);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const getFullName = (account: BorrowerChild | UserView): string =>
    `${account.firstName} ${account.lastName}`;

  const renderGuardianMenuItem = (account: BorrowerChild | UserView) => {
    const fullName = getFullName(account);
    const isSelected = account.id === selected.id;

    return (
      <MenuItem
        key={account.id}
        value={account.id}
        onClick={() => handleSelect(account.id)}
        selected={isSelected}
      >
        <GuardianAvatar
          user={account}
          sx={{ width: 24, height: 24, fontSize: 12, mr: 1 }}
        />

        <Box
          component="span"
          sx={{ fontWeight: isSelected ? "bold" : "normal" }}
        >
          {fullName}
        </Box>
      </MenuItem>
    );
  };

  const menuOpen = Boolean(anchorEl);
  return (
    <Box>
      <Chip
        avatar={<GuardianAvatar user={selected} />}
        label={getFullName(selected)}
        clickable
        onClick={handleClick}
        variant="outlined"
        color="primary"
        deleteIcon={<ArrowDropDownIcon />}
        onDelete={handleClick} //Use the onclick instead but deleteIcon requires this prop
        aria-haspopup="listbox"
        aria-controls="account-selector-menu"
        id="selected-account-chip"
        aria-expanded={menuOpen ? "true" : undefined}
      />

      <Menu
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={handleClose}
        slotProps={{
          paper: { style: { width: 320, padding: 8 } },
          list: {role: "listbox", "aria-labelledby": "selected-account-chip"}
        }}
        id="account-selector-menu"
      >
        {(accountsList || []).map(renderGuardianMenuItem)}
      </Menu>
    </Box>
  );
};

export default AccountGuardianSelector;
